import { type TFunction } from "i18next";
import * as Sentry from "@sentry/react";

interface ResponseCheckerOptions {
  context?: string;
  customErrors?: Record<number, string>;
}

/**
 * Checks HTTP response status and throws appropriate translated error messages
 * @param response - The HTTP response object
 * @param t - Translation function from react-i18next
 * @param options - Configuration options
 * @param options.context - Context for error translations (e.g., 'userSettings', 'fileUpload')
 * @param options.customErrors - Custom error messages for specific status codes
 * @throws Error with translated message based on status code
 */
export const checkResponseStatus = (
  response: Response,
  t: TFunction,
  options: ResponseCheckerOptions = {}
): void => {
  if (response.ok) return;

  const { context = "general", customErrors = {} } = options;

  if (customErrors[response.status]) {
    throw new Error(customErrors[response.status]);
  }

  const getErrorKey = (errorType: string): string => {
    const contextKey = `${context}.${errorType}`;
    const generalKey = `general.${errorType}`;

    try {
      const contextTranslation = t(contextKey);
      if (contextTranslation !== contextKey) {
        return contextKey;
      }
    } catch {
      console.warn(`Translation key not found: ${contextKey}`);
      return generalKey;
    }

    return generalKey;
  };

  const status = response.status;
  const mapAndThrow = (errorType: string) => {
    const message = t(getErrorKey(errorType));
    if (status >= 500 || ![400, 401, 403, 404, 408].includes(status)) {
      Sentry.captureException(new Error(message), {
        tags: {
          context,
          status: String(status),
        },
        extra: {
          url: (() => { try { return new URL(response.url).pathname; } catch { return response.url; } })(),
          statusText: response.statusText,
        }
      });
    }
    throw new Error(message);
  };

  switch (status) {
    case 400:
      return mapAndThrow("badRequest");
    case 401:
      return mapAndThrow("unauthorized");
    case 403:
      return mapAndThrow("forbidden");
    case 404:
      return mapAndThrow("notFound");
    case 408:
      return mapAndThrow("timeout");
    case 500:
    case 502:
    case 503:
    case 504:
      return mapAndThrow("serverError");
    default:
      return mapAndThrow("unknown");
  }
};

/**
 * Higher-order function that creates a response checker with pre-configured context
 * @param context - The context for error translations
 * @returns A function that checks response status with the given context
 */
export const createResponseChecker = (context: string) => {
  return (response: Response, t: TFunction, customErrors?: Record<number, string>) => {
    checkResponseStatus(response, t, { context, customErrors });
  };
};
